import React, { useState, useEffect } from 'react';
import {
    Box,
    Container,
    Typography,
    TextField,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    IconButton,
    Chip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    Button,
    CircularProgress,
    Menu as DropdownMenu,
    MenuItem,
    // FormControl,
    // InputLabel,
    // Select,
    Skeleton,
    TablePagination,
    InputAdornment,
    Autocomplete
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import {
    // Visibility as ViewIcon,
    // Edit as EditIcon,
    MoreVert as MoreVertIcon,
    Search as SearchIcon,
    Clear as ClearIcon
} from '@mui/icons-material';
import { extractList } from '../../utils/response';
import { useLazySelectOptions } from '../../hooks/useLazySelectOptions';
import PageLayout from '../../components/layout/PageLayout';
import {
    useLazyGetDBAsQuery,
    useLazyGetDbaStatusQuery,
    useDeleteDbaMutation,
    useUpdateDbaProcessStatusMutation,
    useAssignDbaToUserMutation,
    useLazyGetUsersListQuery,
    useCreateEquipmentMutation,
    useLazyGetEquipmentStatusesQuery,
    useLazyGetEquipmentTypesQuery,
    useLazyGetManufacturersQuery,
    useLazyGetEquipmentModelsQuery,
} from '../../store/api';
import { toast } from 'react-toastify';
import SweetAlert from '../../utils/sweetAlert';
import Can from '../../components/permissions/Can';
import { useAuth } from '../../hooks/useAuth';
import AddEquipmentModal from '../../components/Merchants/Modal/AddEquipmentModal';
import { getDbaProcessStatusId } from '../../components/Merchants';

const MerchantList = () => {
    const navigate = useNavigate();
    const [fetchDBAs] = useLazyGetDBAsQuery();
    const [fetchDbaStatus] = useLazyGetDbaStatusQuery();
    const [deleteDba] = useDeleteDbaMutation();
    const [updateDbaProcessStatus] = useUpdateDbaProcessStatusMutation();
    const [assignDbaToUser] = useAssignDbaToUserMutation();
    const [fetchUsersList] = useLazyGetUsersListQuery();
    const [createEquipment] = useCreateEquipmentMutation();
    const [fetchEquipmentStatuses] = useLazyGetEquipmentStatusesQuery();
    const [fetchEquipmentTypes] = useLazyGetEquipmentTypesQuery();
    const [fetchManufacturers] = useLazyGetManufacturersQuery();
    const [fetchEquipmentModels] = useLazyGetEquipmentModelsQuery();
    const { hasPermission } = useAuth();

    const canViewMerchants = hasPermission('dba-view');
    const canEditMerchants = hasPermission('dba-edit');
    const canChangeStatus = hasPermission('dba-status');
    const canAssignDBA = hasPermission('dba-reassign');
    const canDeleteMerchants = hasPermission('dba-delete');

    const { options: statusOptions, load: loadStatusOptions } = useLazySelectOptions(
        fetchDbaStatus,
        { args: { 'conditions[model]': 'dba' }, enabled: canViewMerchants },
    );
    const { options: usersList, load: loadUsersList } = useLazySelectOptions(
        fetchUsersList,
        {
            // Assign dialog shows the user name only — no relations needed
            args: { paginate: 0 },
            enabled: canAssignDBA,
        },
    );

    const [dbaData, setDbaData] = useState([]);
    const [tableLoading, setTableLoading] = useState(false);
    const [processingId, setProcessingId] = useState(null);
    const [openStatusDialog, setOpenStatusDialog] = useState(false);
    const [selectedDba, setSelectedDba] = useState(null);
    const [newStatus, setNewStatus] = useState('');
    const [anchorEl, setAnchorEl] = useState(null);
    const [openAssignDialog, setOpenAssignDialog] = useState(false);
    const [selectedUser, setSelectedUser] = useState('');
    const [searchValue, setSearchValue] = useState('');
    const [pagination, setPagination] = useState({
        page: 1,
        pageSize: 10,
        total: 0,
    });


    // add new equipment
    const [openAddEquipment, setOpenAddEquipment] = useState(false);
    const [dbaForNewEquipment, setDbaForNewEquipment] = useState(null);
    const [newEquipment, setNewEquipment] = useState({
        equipment_type_id: '',
        equipment_type_custom: '',
        equipment_model_id: '',
        equipment_model_custom: '',
        manufacturer_id: '',
        manufacturer_custom: '',
        status_id: '',
        sku_number: '',
        add_terminal: '',
        add_gateway: '',
        note: '',
    });
    const {
        options: typeOptions,
        loading: typeOptionsLoading,
        load: loadTypeOptions,
    } = useLazySelectOptions(fetchEquipmentTypes, {
        mapResult: (res) => extractList(res),
    });
    const {
        options: manufacturerOptions,
        loading: manufacturerOptionsLoading,
        load: loadManufacturerOptions,
    } = useLazySelectOptions(fetchManufacturers, {
        mapResult: (res) => extractList(res),
    });
    const {
        options: equipmentStatusOptions,
        loading: equipmentStatusOptionsLoading,
        load: loadEquipmentStatusOptions,
    } = useLazySelectOptions(fetchEquipmentStatuses, {
        mapResult: (res) => (Array.isArray(res) ? res : extractList(res)),
    });
    const [modelOptions, setModelOptions] = useState([]);
    const [createLoading, setCreateLoading] = useState(false);

    const capitalizeWords = (text) => {
        if (!text) return "";
        return text.replace(/\b\w/g, (char) => char.toUpperCase());
    };

    useEffect(() => {
        if (openStatusDialog) loadStatusOptions();
    }, [openStatusDialog, loadStatusOptions]);

    useEffect(() => {
        if (openAssignDialog) loadUsersList();
    }, [openAssignDialog, loadUsersList]);

    // Load equipment models when type and manufacturer are selected
    useEffect(() => {
        if (!newEquipment.equipment_type_id || !newEquipment.manufacturer_id) {
            setModelOptions([]);
            return;
        }
        fetchEquipmentModels({
            equipment_type_id: newEquipment.equipment_type_id,
            manufacturer_id: newEquipment.manufacturer_id,
        })
            .unwrap()
            .then((res) => {
                const list = extractList(res);
                setModelOptions(Array.isArray(list) ? list : []);
            })
            .catch(() => setModelOptions([]));
    }, [newEquipment.equipment_type_id, newEquipment.manufacturer_id, fetchEquipmentModels]);

    const loadDBAData = async (page = pagination.page, pageSize = pagination.pageSize, search = searchValue) => {
        if (!canViewMerchants) {
            toast.error('You do not have permission to view merchants.');
            return;
        }
        try {
            setTableLoading(true);
            const params = {
                // Only what the table renders: owner name, assignee, status chip
                'relations[]': ['dbaOwners', 'lastStatusLog', 'users'],
                'conditions[legal_info_id]': '',
                'conditions[type]': 'normal',
                'status': 'under-writing',
                paginate: 1,
                page: page,
                per_page: pageSize,
                ...(search && { 'search': search })
            };
            const response = await fetchDBAs(params).unwrap();
            setDbaData(extractList(response));
            setPagination(prev => ({
                ...prev,
                total: response?.data?.pagination?.total || 0
            }));
        } catch (error) {
            console.error('Error loading DBA data:', error);
            toast.error('Failed to load DBA data. Please try again.');
        } finally {
            setTableLoading(false);
        }
    };

    useEffect(() => {
        loadDBAData();
        // eslint-disable-next-line react-hooks/exhaustive-deps -- dialog fields must not refetch
    }, [pagination.page, pagination.pageSize]);

    const isFirstSearchEffect = React.useRef(true);
    useEffect(() => {
        if (isFirstSearchEffect.current) {
            isFirstSearchEffect.current = false;
            return;
        }
        const timer = setTimeout(() => {
            if (pagination.page !== 1) {
                setPagination((prev) => ({ ...prev, page: 1 }));
                return;
            }
            loadDBAData(1, pagination.pageSize, searchValue);
        }, 500);

        return () => clearTimeout(timer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchValue]);

    // Handle search input change
    const handleSearchChange = (e) => {
        setSearchValue(e.target.value);
    };

    // Clear search
    const handleClearSearch = () => {
        setSearchValue('');
        setPagination(prev => ({ ...prev, page: 1 }));
    };

    const handlePageChange = (newPage) => {
        setPagination(prev => ({ ...prev, page: newPage }));
    };

    const handleRowsPerPageChange = (newSize) => {
        setPagination({ page: 1, pageSize: newSize, total: pagination.total });
    };

    const handleView = (dba) => {
        navigate(`/view-merchant?id=${dba.id}`);
    };

    const handleEquipemntView = (dba) => {
        navigate(`/merchants/equipments?dba_id=${dba.id}`);
    }

    const handleEdit = (dba) => {
        if (!canEditMerchants) return;
        navigate(`/create-merchant?edit=true&id=${dba.id}`);
    };


    const handleDelete = async (dba) => {
        if (!canDeleteMerchants) {
            toast.error('You do not have permission to delete merchants.');
            return;
        }
        try {
            // Show SweetAlert confirmation
            const result = await SweetAlert.deleteConfirm(dba.name || 'this DBA');

            // If user cancels, return early
            if (!result.isConfirmed) {
                return;
            }

            setProcessingId(dba.id);
            await deleteDba(dba.id).unwrap();
            await SweetAlert.success({
                text: 'DBA record deleted successfully.'
            });
            await loadDBAData();
        } catch (error) {
            console.error('Delete action error:', error);
            await SweetAlert.error({
                text: 'Failed to delete DBA record'
            });
        } finally {
            setProcessingId(null);
        }
    };


    const handleOpenStatusDialog = (dba) => {
        setSelectedDba(dba);
        setNewStatus(getDbaProcessStatusId(dba));
        setOpenStatusDialog(true);
    };

    const handleCloseStatusDialog = () => {
        setOpenStatusDialog(false);
        setSelectedDba(null);
        setNewStatus('');
    };

    const handleChangeStatus = async () => {
        if (!canChangeStatus) {
            toast.error('You do not have permission to change merchant status.');
            return;
        }
        if (!selectedDba || !newStatus) {
            toast.warn('Please select a status.');
            return;
        }

        try {
            setProcessingId(selectedDba.id);
            const response = await updateDbaProcessStatus({ id: selectedDba.id, statusId: newStatus }).unwrap();

            if (response?.success) {
                setDbaData((prev) =>
                    prev.map((dba) =>
                        dba.id === selectedDba.id
                            ? {
                                ...dba,
                                status: {
                                    ...dba.status,
                                    id: newStatus,
                                    name: statusOptions.find((option) => option.id === newStatus)?.name || dba.status?.name
                                }
                            }
                            : dba
                    )
                );

                toast.success('Status updated successfully');
                handleCloseStatusDialog();
            } else {
                throw new Error(response.message || 'Failed to update status');
            }
        } catch (error) {
            console.error('Error updating status:', error);
            toast.error('Failed to update status. Please try again.');
        } finally {
            setProcessingId(null);
        }
    };

    const handleAssign = async () => {
        if (!canAssignDBA) {
            toast.error('You do not have permission to assign merchants.');
            return;
        }
        if (!selectedDba || !selectedUser) {
            toast.warn('Please select a user.');
            return;
        }

        try {
            setProcessingId(selectedDba.id);
            const response = await assignDbaToUser({ id: selectedDba.id, userId: selectedUser }).unwrap();

            if (response.success) {
                const assignedUser = usersList.find(user => user.id === selectedUser);

                setDbaData((prev) =>
                    prev.map((dba) =>
                        dba.id === selectedDba.id
                            ? { ...dba, users: { ...dba.users?.name, name: assignedUser?.name || 'Assigned' } }
                            : dba
                    )
                );

                toast.success('DBA assigned successfully');
                setOpenAssignDialog(false);
                setSelectedUser('');
            } else {
                throw new Error(response.message || 'Failed to assign DBA');

            }
        } catch (error) {
            console.error('Error assigning DBA:', error);
            toast.error('Failed to assign DBA. Please try again.');
        } finally {
            setProcessingId(null);
        }
    };

    const handleAddEquipmentConfirm = async () => {
        if (!dbaForNewEquipment?.id) {
            toast.error('No merchant selected. Please select a row and try again.');
            return;
        }
        const payload = {
            dba_id: dbaForNewEquipment.id,
            equipment_type_id: newEquipment.equipment_type_id ?? '',
            equipment_type_custom: newEquipment.equipment_type_custom ?? '',
            equipment_model_id: newEquipment.equipment_model_id ?? '',
            equipment_model_custom: newEquipment.equipment_model_custom ?? '',
            manufacturer_id: newEquipment.manufacturer_id ?? '',
            manufacturer_custom: newEquipment.manufacturer_custom ?? '',
            status_id: newEquipment.status_id,
            sku_number: newEquipment.sku_number ?? '',
            add_terminal: newEquipment.add_terminal ?? '',
            add_gateway: newEquipment.add_gateway ?? '',
            note: newEquipment.note ?? '',
        };
        try {
            setCreateLoading(true);
            await createEquipment(payload).unwrap();
            toast.success('Equipment added successfully.');
        setNewEquipment({
            equipment_type_id: '',
            equipment_type_custom: '',
            equipment_model_id: '',
            equipment_model_custom: '',
            manufacturer_id: '',
            manufacturer_custom: '',
            status_id: '',
            sku_number: '',
            add_terminal: '',
            add_gateway: '',
            note: '',
        });
        setOpenAddEquipment(false);
            setDbaForNewEquipment(null);
            loadDBAData();
        } catch (err) {
            toast.error(err?.message || err || 'Failed to add equipment.');
        } finally {
            setCreateLoading(false);
        }
    };

    const handleOpenAddEquipment = (dba) => {
        setDbaForNewEquipment(dba || null);
        setNewEquipment({
            equipment_type_id: '',
            equipment_type_custom: '',
            equipment_model_id: '',
            equipment_model_custom: '',
            manufacturer_id: '',
            manufacturer_custom: '',
            status_id: '',
            sku_number: '',
            add_terminal: '',
            add_gateway: '',
            note: '',
        });
        setModelOptions([]);
        setOpenAddEquipment(true);
    };

    return (
        <PageLayout>
            <Container maxWidth="xl" sx={{ py: 3 }}>
                <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography
                        variant="h4"
                        sx={{ color: '#154A8A', fontWeight: 600 }}
                    >
                        Merchants Accounts List ({dbaData.length})
                    </Typography>

                    <TextField
                        placeholder="Search by Merchant DBA"
                        value={searchValue}
                        onChange={handleSearchChange}
                        sx={{ width: 300 }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon color="action" />
                                </InputAdornment>
                            ),
                            endAdornment: searchValue && (
                                <InputAdornment position="end">
                                    <IconButton
                                        size="small"
                                        onClick={handleClearSearch}
                                        edge="end"
                                    >
                                        <ClearIcon />
                                    </IconButton>
                                </InputAdornment>
                            ),
                        }}
                    />
                </Box>

                <Paper sx={{ boxShadow: 1 }}>
                    <TableContainer>
                        <Table>
                            <TableHead>
                                <TableRow sx={{ bgcolor: 'grey.100' }}>
                                    <TableCell sx={{ fontWeight: 600, color: 'grey.700' }}>
                                        Business Name
                                    </TableCell>
                                    <TableCell sx={{ fontWeight: 600, color: 'grey.700' }}>
                                        Owner Name
                                    </TableCell>
                                    <TableCell sx={{ fontWeight: 600, color: 'grey.700' }}>
                                        Business Email
                                    </TableCell>
                                    <TableCell sx={{ fontWeight: 600, color: 'grey.700' }}>
                                        Business Phone
                                    </TableCell>
                                    <TableCell sx={{ fontWeight: 600, color: 'grey.700' }}>
                                        Status
                                    </TableCell>
                                    <TableCell sx={{ fontWeight: 600, color: 'grey.700' }}>
                                        Created At
                                    </TableCell>
                                    <TableCell sx={{ fontWeight: 600, color: 'grey.700', textAlign: 'center' }}>Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {tableLoading ? (
                                    // Skeleton loading rows
                                    [...Array(pagination.pageSize)].map((_, index) => (
                                        <TableRow key={index}>
                                            <TableCell><Skeleton animation="wave" height={30} /></TableCell>
                                            <TableCell><Skeleton animation="wave" height={30} /></TableCell>
                                            <TableCell><Skeleton animation="wave" height={30} /></TableCell>
                                            <TableCell><Skeleton animation="wave" height={30} /></TableCell>
                                            <TableCell><Skeleton animation="wave" height={30} /></TableCell>
                                            <TableCell><Skeleton animation="wave" height={30} width={80} /></TableCell>
                                            <TableCell sx={{ textAlign: 'center' }}><Skeleton animation="wave" height={30} width={40} /></TableCell>
                                        </TableRow>
                                    ))
                                ) : dbaData.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={7} sx={{ textAlign: 'center', py: 4 }}>
                                            <Typography variant="body1" color="text.secondary">
                                                {searchValue ? 'No records found for your search' : "There's no records to display"}
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    dbaData.map((dba) => (
                                        <TableRow key={dba.id} hover>
                                            <TableCell>{dba.name || 'N/A'}</TableCell>
                                            <TableCell>{dba?.dba_Owners?.[0]?.full_name || 'N/A'}</TableCell>
                                            <TableCell>{dba?.email || 'N/A'}</TableCell>
                                            <TableCell>{dba?.phone || 'Unassigned'}</TableCell>
                                            <TableCell>
                                                <Chip sx={{textTransform: 'capitalize'}}
                                                    label={dba?.last_status_log?.status?.name || 'undefine'}
                                                    color={dba?.last_status_log?.status?.name === 'Completed' ? 'success' : dba?.last_status_log?.status?.name === 'Underwriting' ? 'warning' : 'default'}
                                                    size="small"
                                                />
                                            </TableCell>
                                            <TableCell>{new Date(dba.created_at || dba.updated_at).toLocaleDateString()}</TableCell>
                                            <TableCell sx={{ textAlign: 'center' }}>
                                                <Can permission="dba-view" fallback={<Box sx={{ width: 40, display: 'inline-block' }} />}>
                                                    <IconButton
                                                        onClick={(event) => {
                                                            setSelectedDba(dba);
                                                            setAnchorEl(event.currentTarget);
                                                        }}
                                                        disabled={processingId === dba.id}
                                                    >
                                                        <MoreVertIcon />
                                                    </IconButton>
                                                </Can>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    {/* Pagination */}
                    {!tableLoading && dbaData.length > 0 && (
                        <TablePagination
                            component="div"
                            count={pagination.total}
                            page={pagination.page - 1}
                            onPageChange={(_, newPage) => {
                                handlePageChange(newPage + 1);
                            }}
                            rowsPerPage={pagination.pageSize}
                            onRowsPerPageChange={(e) => {
                                handleRowsPerPageChange(Number(e.target.value));
                            }}
                            rowsPerPageOptions={[5, 10, 25, 50]}
                            labelRowsPerPage="Rows per page"
                        />
                    )}
                </Paper>

                <DropdownMenu
                    anchorEl={anchorEl}
                    open={Boolean(anchorEl)}
                    onClose={() => setAnchorEl(null)}
                >
                    <Can permission="dba-view">
                        <MenuItem onClick={() => {
                            handleView(selectedDba);
                            setAnchorEl(null);
                        }}>
                            View
                        </MenuItem>
                    </Can>

                    <Can permission="dba-edit">
                        <MenuItem onClick={() => {
                            handleEdit(selectedDba);
                            setAnchorEl(null);
                        }}>
                            Edit
                        </MenuItem>
                    </Can>

                    <Can permission="dba-status">
                        <MenuItem onClick={() => {
                            handleOpenStatusDialog(selectedDba);
                            setAnchorEl(null);
                        }}>
                            Change Status
                        </MenuItem>
                    </Can>

                    {/* new dropdowns */}
                    <Can permission={"dba-list"}>
                        <MenuItem
                            onClick={() => {
                                handleEquipemntView(selectedDba);
                                setAnchorEl(null);
                            }}
                        >
                            View Equipment
                        </MenuItem>
                    </Can>

                    <Can permission={"dba-list"}>
                        <MenuItem onClick={() => {
                            handleOpenAddEquipment(selectedDba);
                            setAnchorEl(null);
                        }}>
                            Add New Equipment
                        </MenuItem>
                    </Can>

                    {/* end dropdowns */}


                    <Can permission="dba-reassign">
                        <MenuItem onClick={() => {
                            setOpenAssignDialog(true);
                            setAnchorEl(null);
                        }}>
                            Assign DBA to User
                        </MenuItem>
                    </Can>

                    <Can permission="dba-delete">
                        <MenuItem onClick={() => {
                            handleDelete(selectedDba);
                            setAnchorEl(null);
                        }}>
                            Delete
                        </MenuItem>
                    </Can>



                </DropdownMenu>

                <Dialog
                    open={openStatusDialog}
                    onClose={handleCloseStatusDialog}
                    maxWidth="sm"
                    fullWidth
                    sx={{
                        '& .MuiDialog-paper': {
                            borderRadius: 2,
                            boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
                            p: 2
                        }
                    }}
                >
                    <DialogTitle sx={{
                        fontSize: '1.5rem',
                        fontWeight: 600,
                        color: '#154A8A',
                        pb: 1,
                        borderBottom: '1px solid',
                        borderColor: 'grey.200'
                    }}>
                        Change DBA Status
                    </DialogTitle>
                    <DialogContent sx={{ pt: 3, pb: 2 }}>
                        <DialogContentText sx={{ mb: 2, color: 'grey.700' }}>
                            Update the status for {selectedDba?.name || 'this DBA'}. Select a new status below.
                        </DialogContentText>

                        <Autocomplete
                            options={statusOptions}
                            getOptionLabel={(option) => capitalizeWords(option.name || "")}
                            value={
                            statusOptions.find((status) => status.id === newStatus) || null
                            }
                            onChange={(event, newValue) => {
                            setNewStatus(newValue ? newValue.id : "");
                            }}
                            renderInput={(params) => (
                            <TextField
                                {...params}
                                label="Change the DBA status to"
                                fullWidth
                            />
                            )}
                            sx={{
                            bgcolor: "white",
                            borderRadius: 1
                            }}
                        />
                        </DialogContent>
                    <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
                        <Button
                            onClick={handleCloseStatusDialog}
                            variant="outlined"
                            color="secondary"
                            sx={{
                                borderColor: 'grey.400',
                                color: 'grey.700',
                                '&:hover': { borderColor: 'grey.600', bgcolor: 'grey.50' }
                            }}
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={handleChangeStatus}
                            variant="contained"
                            color="primary"
                            disabled={!newStatus || processingId === selectedDba?.id}
                            startIcon={processingId === selectedDba?.id ? <CircularProgress size={20} /> : null}
                            sx={{
                                bgcolor: '#154A8A',
                                '&:hover': { bgcolor: '#0d3a6a' },
                                '&:disabled': { bgcolor: '#154A8A', opacity: 0.5 }
                            }}
                        >
                            Change Status
                        </Button>
                    </DialogActions>
                </Dialog>

                <Dialog
                    open={openAssignDialog}
                    onClose={() => setOpenAssignDialog(false)}
                    maxWidth="sm"
                    fullWidth
                    sx={{
                        '& .MuiDialog-paper': {
                            borderRadius: 2,
                            boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
                            p: 2
                        }
                    }}
                >
                    <DialogTitle sx={{
                        fontSize: '1.5rem',
                        fontWeight: 600,
                        color: '#154A8A',
                        pb: 1,
                        borderBottom: '1px solid',
                        borderColor: 'grey.200'
                    }}>
                        Assign DBA to User
                    </DialogTitle>
                    <DialogContent sx={{ pt: 3, pb: 2 }}>
                        <DialogContentText sx={{ mb: 2, color: 'grey.700' }}>
                            Assign {selectedDba?.name || 'this DBA'} to a user. Search and select a user below.
                        </DialogContentText>

                        <Autocomplete
                            options={usersList}
                            getOptionLabel={(option) => option.name}
                            value={usersList.find(user => user.id === selectedUser) || null}
                            onChange={(event, newValue) => {
                                setSelectedUser(newValue?.id || '');
                            }}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Search User"
                                    variant="outlined"
                                    placeholder="Type to search..."
                                    sx={{ bgcolor: 'white' }}
                                />
                            )}
                            renderOption={(props, option) => (
                                <Box component="li" {...props} key={option.id}>
                                    {option.name}
                                </Box>
                            )}
                            sx={{ mt: 1 }}
                            fullWidth
                            disablePortal={false}
                            clearOnEscape
                        />
                    </DialogContent>
                    <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
                        <Button
                            onClick={() => setOpenAssignDialog(false)}
                            variant="outlined"
                            color="secondary"
                            sx={{
                                borderColor: 'grey.400',
                                color: 'grey.700',
                                '&:hover': { borderColor: 'grey.600', bgcolor: 'grey.50' }
                            }}
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={handleAssign}
                            variant="contained"
                            color="primary"
                            disabled={!selectedUser || processingId === selectedDba?.id}
                            startIcon={processingId === selectedDba?.id ? <CircularProgress size={20} /> : null}
                            sx={{
                                bgcolor: '#154A8A',
                                '&:hover': { bgcolor: '#0d3a6a' },
                                '&:disabled': { bgcolor: '#154A8A', opacity: 0.5 }
                            }}
                        >
                            Assign
                        </Button>
                    </DialogActions>
                </Dialog>


                {/* add machine dialog */}
                <AddEquipmentModal
                    open={openAddEquipment}
                    onClose={() => {
                        setOpenAddEquipment(false);
                        setDbaForNewEquipment(null);
                    }}
                    equipment={newEquipment}
                    onEquipmentChange={setNewEquipment}
                    typeOptions={typeOptions}
                    manufacturerOptions={manufacturerOptions}
                    modelOptions={modelOptions}
                    statusOptions={equipmentStatusOptions}
                    typeOptionsLoading={typeOptionsLoading}
                    manufacturerOptionsLoading={manufacturerOptionsLoading}
                    statusOptionsLoading={equipmentStatusOptionsLoading}
                    onOpenTypeOptions={loadTypeOptions}
                    onOpenManufacturerOptions={loadManufacturerOptions}
                    onOpenStatusOptions={loadEquipmentStatusOptions}
                    processing={createLoading}
                    onConfirm={handleAddEquipmentConfirm}
                    title="Add New Equipment"
                />

            </Container>
        </PageLayout>
    );
};

export default MerchantList;